import client, { ClientOptions } from "../client";
import internalClient from "../internalClient";

export type Workflow = {
  workflow_id: string;
  display_id: string;
  organisation_id: string;
  subsystem: string;
  entity_type: string;
  name: string;
  description?: string | null;
  priority: number;
  from_state: string;
  to_state: string;
  action: string;
  scope?: string | null;
  condition?: string | null;
  // Q5 boolean condition tree (AWSJSON string). A rule stores exactly one of
  // condition / condition_tree — never both.
  condition_tree?: string | null;
  // Tree SCHEDULER rules only (AWSJSON); absent = DAILY. Flat rules keep
  // their cadence in condition.frequency.
  frequency?: string | null;
  action_params?: string | null;
  active: boolean;
  version: string;
  // Set when the workflow was activated from a SYSTEM template — carries the
  // template's workflow_id. Create-time only, never updated.
  source_template_id?: string | null;
  created_at: string;
  updated_at: string;
  created_by?: string | null;
  updated_by?: string | null;
};

export interface WorkflowDefinitionFilter {
  subsystem?: string;
  subsystems?: string[];
  entity_type?: string;
  entity_types?: string[];
  active?: boolean;
  query?: string;
  // When true, the secured API returns SYSTEM-org workflow templates instead
  // of the caller's own workflows. Used by the clone-from-template picker.
  fetchTemplates?: boolean;
}

export interface InternalWorkflowDefinitionFilter {
  subsystem?: string;
  subsystems?: string[];
  entity_type?: string;
  entity_types?: string[];
  active?: boolean;
  query?: string;
  organisation_id?: string;
}

export interface SearchWorkflowRequest {
  from?: number;
  size?: number;
  sort_by?: string;
  sort?: "asc" | "desc";
  workflowFilter?: WorkflowDefinitionFilter;
}

export interface InternalSearchWorkflowRequest {
  from?: number;
  size?: number;
  sort_by?: string;
  sort?: "asc" | "desc";
  workflowFilter?: InternalWorkflowDefinitionFilter;
}

export const searchWorkflow = (data?: SearchWorkflowRequest, options?: ClientOptions) => {
  const input: Record<string, unknown> = {
    from: data?.from,
    size: data?.size,
    filter: {
      workflow_definition_filter: data?.workflowFilter
    }
  };

  if (data?.sort_by) input.sort_by = data.sort_by;
  if (data?.sort) input.sort = data.sort;

  return client(
    {
      query: `
      query SearchWorkflow($input: SearchInput) {
        search_workflow(input: $input) {
          total_pages
          total_size
          items {
            workflow_id
            display_id
            organisation_id
            subsystem
            entity_type
            name
            description
            priority
            from_state
            to_state
            action
            scope
            condition
            condition_tree
            frequency
            action_params
            active
            version
            source_template_id
            created_at
            updated_at
            created_by
            updated_by
          }
        }
      }
    `,
      variables: { input }
    },
    options
  );
};

export interface SearchWorkflowTemplatesOptions {
  // Template-level retirement: inactive SYSTEM templates are withdrawn from
  // both the clone picker and the Predefined catalogue by default.
  activeOnly?: boolean;
}

// Fetch workflow templates (SYSTEM-org) for the clone-from-template picker on
// the New Workflow page and the Predefined catalogue.
export const searchWorkflowTemplates = ({ activeOnly = true }: SearchWorkflowTemplatesOptions = {}) =>
  searchWorkflow({
    from: 0,
    size: 100,
    workflowFilter: activeOnly ? { fetchTemplates: true, active: true } : { fetchTemplates: true }
  });

export const internalSearchWorkflow = (data?: InternalSearchWorkflowRequest) => {
  const input: Record<string, unknown> = {
    from: data?.from,
    size: data?.size,
    filter: {
      workflow_definition_filter: data?.workflowFilter
    }
  };

  if (data?.sort_by) input.sort_by = data.sort_by;
  if (data?.sort) input.sort = data.sort;

  return internalClient({
    query: `
      query InternalSearchWorkflow($input: SearchInput) {
        internal_search_workflow(input: $input) {
          total_pages
          total_size
          items {
            workflow_id
            display_id
            organisation_id
            subsystem
            entity_type
            name
            description
            priority
            from_state
            to_state
            action
            scope
            condition
            condition_tree
            frequency
            action_params
            active
            version
            created_at
            updated_at
            created_by
            updated_by
          }
        }
      }
    `,
    variables: { input }
  });
};

export const getWorkflow = (workflow_id: string) => {
  return client({
    query: `
      query GetWorkflow($workflow_id: String!) {
        get_workflow(workflow_id: $workflow_id) {
          workflow_id
          display_id
          organisation_id
          subsystem
          entity_type
          name
          description
          priority
          from_state
          to_state
          action
          scope
          condition
          condition_tree
          frequency
          action_params
          active
          version
          created_at
          updated_at
          created_by
          updated_by
        }
      }
    `,
    variables: { workflow_id }
  });
};

export interface InternalGetWorkflowInput {
  workflow_id: string;
  organisation_id: string;
}

export const internalGetWorkflow = (input: InternalGetWorkflowInput) => {
  return internalClient({
    query: `
      query InternalGetWorkflow($input: InternalGetWorkflowInput!) {
        internal_get_workflow(input: $input) {
          workflow_id
          display_id
          organisation_id
          subsystem
          entity_type
          name
          description
          priority
          from_state
          to_state
          action
          scope
          condition
          condition_tree
          frequency
          action_params
          active
          version
          created_at
          updated_at
          created_by
          updated_by
        }
      }
    `,
    variables: { input }
  });
};

export interface CreateWorkflowInput {
  subsystem: string;
  entity_type: string;
  name: string;
  description?: string;
  priority: number;
  from_state?: string;
  to_state?: string;
  action: string;
  scope?: string;
  condition?: string;
  condition_tree?: string;
  frequency?: string;
  action_params: string;
  source_template_id?: string;
}

export const createWorkflow = (input: CreateWorkflowInput) => {
  return client({
    query: `
      mutation CreateWorkflow($input: CreateWorkflowInput!) {
        create_workflow(input: $input) {
          workflow_id
          display_id
          organisation_id
          subsystem
          entity_type
          name
          description
          priority
          from_state
          to_state
          action
          scope
          condition
          condition_tree
          frequency
          action_params
          active
          version
          source_template_id
          created_at
          updated_at
          created_by
          updated_by
        }
      }
    `,
    variables: { input }
  });
};

export const createWorkflowTemplate = (input: CreateWorkflowInput) => {
  return internalClient({
    query: `
      mutation CreateWorkflowTemplate($input: CreateWorkflowTemplateInput!) {
        internal_create_workflow_template(input: $input) {
          workflow_id
          display_id
          organisation_id
          subsystem
          entity_type
          name
          description
          priority
          from_state
          to_state
          action
          scope
          condition
          condition_tree
          frequency
          action_params
          active
          version
          created_at
          updated_at
          created_by
          updated_by
        }
      }
    `,
    variables: { input }
  });
};

export interface UpdateWorkflowInput {
  workflow_id: string;
  version: string;
  name?: string;
  description?: string;
  priority?: number;
  from_state?: string;
  to_state?: string;
  action?: string;
  scope?: string;
  condition?: string;
  condition_tree?: string;
  frequency?: string;
  action_params?: string;
}

export const updateWorkflow = (input: UpdateWorkflowInput, options?: ClientOptions) => {
  return client(
    {
      query: `
      mutation UpdateWorkflow($input: UpdateWorkflowInput!) {
        update_workflow(input: $input) {
          workflow_id
          display_id
          organisation_id
          subsystem
          entity_type
          name
          description
          priority
          from_state
          to_state
          action
          scope
          condition
          condition_tree
          frequency
          action_params
          active
          version
          created_at
          updated_at
          created_by
          updated_by
        }
      }
    `,
      variables: { input }
    },
    options
  );
};

export const internalUpdateWorkflowTemplate = (input: UpdateWorkflowInput) => {
  return internalClient({
    query: `
      mutation InternalUpdateWorkflowTemplate($input: UpdateWorkflowTemplateInput!) {
        internal_update_workflow_template(input: $input) {
          workflow_id
          display_id
          organisation_id
          subsystem
          entity_type
          name
          description
          priority
          from_state
          to_state
          action
          scope
          condition
          condition_tree
          frequency
          action_params
          active
          version
          created_at
          updated_at
          created_by
          updated_by
        }
      }
    `,
    variables: { input }
  });
};

export interface ToggleWorkflowStatusInput {
  workflow_id: string;
  version: string;
  active: boolean;
}

export const toggleWorkflowStatus = (input: ToggleWorkflowStatusInput) => {
  return client({
    query: `
      mutation ToggleWorkflowStatus($input: ToggleWorkflowStatusInput!) {
        toggle_workflow_status(input: $input) {
          workflow_id
          display_id
          organisation_id
          subsystem
          entity_type
          name
          description
          priority
          from_state
          to_state
          action
          scope
          condition
          condition_tree
          frequency
          action_params
          active
          version
          created_at
          updated_at
          created_by
          updated_by
        }
      }
    `,
    variables: { input }
  });
};

export const internalToggleWorkflowTemplateStatus = (input: ToggleWorkflowStatusInput) => {
  return internalClient({
    query: `
      mutation InternalToggleWorkflowTemplateStatus($input: ToggleWorkflowTemplateStatusInput!) {
        internal_toggle_workflow_template_status(input: $input) {
          workflow_id
          display_id
          organisation_id
          subsystem
          entity_type
          name
          description
          priority
          from_state
          to_state
          action
          scope
          condition
          condition_tree
          frequency
          action_params
          active
          version
          created_at
          updated_at
          created_by
          updated_by
        }
      }
    `,
    variables: { input }
  });
};
